/**
 * Warehouse task queue — shared domain types for API and UI.
 */

export const TASK_STATUSES = [
  'proposed',
  'available',
  'assigned',
  'released',
  'completed',
  'superseded',
  'archived',
] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TASK_SOURCES = ['one_time', 'recurring'] as const;
export type TaskSource = (typeof TASK_SOURCES)[number];

export const TASK_CADENCES = ['daily', 'weekly', 'monthly'] as const;
export type TaskCadence = (typeof TASK_CADENCES)[number];

export const TASK_AUDIT_ENTITY_TYPES = ['task', 'recurring_task_def', 'tag'] as const;
export type TaskAuditEntityType = (typeof TASK_AUDIT_ENTITY_TYPES)[number];

export const TASK_AUDIT_ACTIONS = [
  'create',
  'update',
  'assign',
  'release',
  'complete',
  'archive',
  'supersede',
  'approve',
  'reopen',
] as const;
export type TaskAuditAction = (typeof TASK_AUDIT_ACTIONS)[number];

export const TASK_AUDIT_SOURCES = ['ui', 'viktor', 'intake', 'scheduler'] as const;
export type TaskAuditSource = (typeof TASK_AUDIT_SOURCES)[number];

/** Open tasks shown in the default worker list. */
export const TASK_OPEN_STATUSES = ['available', 'released', 'assigned'] as const;
export type TaskOpenStatus = (typeof TASK_OPEN_STATUSES)[number];

export interface TaskTag {
  readonly id: string;
  readonly name: string;
  readonly retired: boolean;
}

export interface TaskAssignee {
  readonly id: string;
  readonly name: string;
}

export interface TaskListRow {
  readonly id: string;
  readonly title: string;
  readonly description: string | null;
  readonly status: TaskStatus;
  readonly assigneeId: string | null;
  readonly assigneeName: string | null;
  readonly priority: number;
  readonly source: TaskSource;
  readonly slackPermalink: string | null;
  readonly createdBy: string;
  readonly createdByName: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly tagIds: readonly string[];
  readonly tagNames: readonly string[];
}

export interface TaskComment {
  readonly id: string;
  readonly taskId: string;
  readonly authorId: string;
  readonly authorName: string | null;
  readonly body: string;
  readonly createdAt: string;
}

export interface TaskAuditEntry {
  readonly id: string;
  readonly entityType: TaskAuditEntityType;
  readonly entityId: string;
  readonly actorId: string | null;
  readonly actorName: string | null;
  readonly action: TaskAuditAction;
  readonly changes: Record<string, unknown>;
  readonly source: TaskAuditSource;
  readonly approvalRef: string | null;
  readonly createdAt: string;
}

export interface TaskDetail extends TaskListRow {
  readonly spec: string | null;
  readonly recurringDefId: string | null;
  readonly periodKey: string | null;
  readonly updatedBy: string;
  readonly completedAt: string | null;
  readonly comments: readonly TaskComment[];
}

export interface RecurringTaskDef {
  readonly id: string;
  readonly title: string;
  readonly description: string | null;
  readonly spec: string | null;
  readonly cadence: TaskCadence;
  readonly priority: number;
  readonly active: boolean;
  readonly approvedAt: string | null;
  readonly archivedAt: string | null;
  readonly createdBy: string;
  readonly updatedBy: string;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly tagIds: readonly string[];
  readonly tagNames: readonly string[];
}

export type TaskLifecycleAction =
  | 'approve'
  | 'assign'
  | 'release'
  | 'reassign'
  | 'complete'
  | 'archive'
  | 'reopen';
